import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useSearchOrganizationsAndEntriesQuery } from "../../autogen/bff-api";
import { NO_SUPPLIER_FILTERS, type SupplierFilters, supplierFilterSearchArgs } from "./supplier-filters/supplierFilters";

export const useOrganizationSearch = ({
  name,
  limit = 20,
  skip = 0,
  excludeLinkedEntries = false,
  matchOrganizationNumber = false,
  supplierFilters = NO_SUPPLIER_FILTERS,
  withRelations,
}: {
  excludedOrgId?: string;
  name?: string;
  skip?: number;
  limit?: number;
  excludeLinkedEntries?: boolean;
  matchOrganizationNumber?: boolean;
  supplierFilters?: SupplierFilters;
  withRelations?: boolean;
}) => {
  const [debouncedName, setDebouncedName] = useState<string | undefined>();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedName = useCallback(
    debounce((name) => setDebouncedName(name), 300),
    []
  );

  useEffect(() => {
    handleDebouncedName(name);
  }, [handleDebouncedName, name]);

  const organizationNumber = matchOrganizationNumber ? asOrganizationNumber(debouncedName) : undefined;
  const { data, currentData, isFetching, isError, refetch } = useSearchOrganizationsAndEntriesQuery({
    name: organizationNumber ? undefined : debouncedName,
    organizationNumber,
    limit,
    skip,
    excludeLinkedEntries,
    ...supplierFilterSearchArgs(supplierFilters),
    withRelations,
  });

  return {
    organizationCombinations: data?.organizations,
    searchName: (name: string) => {
      handleDebouncedName(name);
    },
    isSearching: isFetching,
    count: data?.count,
    currentData,
    isError,
    refetch,
  };
};

export const asOrganizationNumber = (text: string | undefined): string | undefined => {
  const digits = text?.replace(/\s/g, "");
  return digits && /^\d{9}$/.test(digits) ? digits : undefined;
};
